<template>
    <Upload
        :action="uploadAction"
        :data="formData"
        :show-upload-list="false"
        :show-progress="true"
        :on-success="onSuccess"
        :on-progress="onProgress"
        :multiple="true"
    >
        <Button type="primary" :data-testid="dataTestid" @click="updatePostData">{{ label }}</Button>
    </Upload>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { MasterTemplateFile } from "@/types/types";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

import clientMasterTemplatesUploadConfigQuery from "@/apollo/queries/v2/ClientMasterTemplatesUploadConfig.gql";
import bus from "@/bus";

import MasterTemplates from "@/services/MasterTemplates";
import { MasterTemplateLibraryAction } from "@/store/modules/masterTemplateLibrary";
import type { InputAEComposition } from "@/types";
import { uploadConfigExpired } from "@/utils";
import { createAEProject } from "@/utils/ae";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { GA4_EVENT_TYPES, GA4_FEATURE_CATEGORIES, GA4_FEATURE_CONTEXTS } from "@/enums/ga4Events";

import { MasterTemplateEvents, MasterTemplateUploadStatus } from "@/enums/masterTemplate";

export default defineComponent({
    name: "MasterTemplateUploadButton",

    inject: {
        refetchMasterTemplates: {
            from: "refetchMasterTemplates",
            default: null as (() => void) | null
        }
    },
    props: {
        label: {
            type: String,
            required: true
        },
        dataTestid: {
            type: String,
            required: false,
            default: ""
        }
    },

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isLoading: false,
            formData: {},
            masterTemplateService: null as null | MasterTemplates
        };
    },

    computed: {
        canManageMasterTemplates() {
            return this.$auth.userCan(this.$auth.Actions.CanManageMasterTemplates, { clientId: this.clientId }, false);
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        persistentBucket() {
            if (!this.uploadConfig) {
                return "";
            }
            // Compute the persistent bucket name in this way for simplicity, based on the temporary bucket name
            // so we avoid the complexity of fetching it from the BE, at least for now. It's harmless and the chances
            // to break something in the feature are linked to the probability to rename the buckets, so extremely low.
            return this.uploadConfig.action
                .split("//")[1]
                .split(".s3-eu-west-1.amazonaws.com")[0]
                .replace("temporary", "persistent");
        },

        uploadAction() {
            return this.uploadConfig?.action || "";
        },

        uploadConfig() {
            return this.uploadConfigObject?.config;
        },

        uploadConfigObject() {
            return this.$store.state.masterTemplateLibrary.uploadConfig;
        }
    },

    created() {
        /*
        Trigger the refetching of the upload config in case it has expired. The skip() check
        in the query definition will stop it from being executed if its not needed.
        */
        this.$apollo.queries.masterTemplateUploadConfig.refetch();

        // @ts-expect-error known issue
        this.masterTemplateService = new MasterTemplates(this.$apollo, this.clientId);
    },

    methods: {
        findDuplicateLayerNames(compositions: InputAEComposition[]) {
            return compositions.reduce((acc, composition) => {
                const layerMap = new Map<string, Set<string>>();

                composition.layers.forEach(layer => {
                    if (!layerMap.has(layer.name)) {
                        layerMap.set(layer.name, new Set([layer.id]));
                    } else {
                        const idSet = layerMap.get(layer.name)!;
                        if (!idSet.has(layer.id)) {
                            acc.push(layer.name);
                        }
                        idSet.add(layer.id);
                    }
                });

                return acc;
            }, [] as string[]);
        },

        async createPlainlyProject(masterTemplateId: string, key: string, file: MasterTemplateFile) {
            try {
                const { plainlyProjectId, compositions } = await createAEProject({
                    clientId: this.clientId,
                    persistentBucket: this.persistentBucket,
                    key
                });

                const duplicates = this.findDuplicateLayerNames(compositions);

                if (duplicates.length) {
                    throw new Error(`Duplicate layer names found: ${duplicates.join(", ")}`);
                }

                await this.masterTemplateService?.completeAE(masterTemplateId, plainlyProjectId, compositions);

                bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                    ...file,
                    status: MasterTemplateUploadStatus.UPLOADED,
                    adType: TemplateType.AE
                });
            } catch (e) {
                await this.masterTemplateService?.delete(masterTemplateId);

                bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                    ...file,
                    status: MasterTemplateUploadStatus.FAILED,
                    adType: TemplateType.AE,
                    errorMessage: "Failed to create plainly project: " + (e as Error).message
                });
            }
        },

        async createTemplate(zipUrl: string, file: MasterTemplateFile) {
            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                const response = await this.masterTemplateService.add(zipUrl);
                const masterTemplate = response.data.addMasterTemplate;

                if (masterTemplate.adType === TemplateType.AE) {
                    this.createPlainlyProject(masterTemplate._id, masterTemplate.persistentKey, file);
                } else {
                    bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                        ...file,
                        status: MasterTemplateUploadStatus.UPLOADED,
                        adType: masterTemplate.adType
                    });
                }

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4_FEATURE_CATEGORIES.MASTER_TEMPLATES,
                    featureContext: GA4_FEATURE_CONTEXTS.ADD_TEMPLATE,
                    freeformValues: [masterTemplate.adType, masterTemplate._id, "Template uploaded successfully"]
                });
            } catch (e) {
                bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                    ...file,
                    status: MasterTemplateUploadStatus.FAILED,
                    errorMessage: (e as Error).message
                });
            }
        },

        onSuccess(_: unknown, file: MasterTemplateFile) {
            const zipUrl = `${this.uploadConfig?.filePrefix}/${file.name}`;

            this.createTemplate(zipUrl, file);
        },

        onProgress(_: unknown, file: MasterTemplateFile) {
            bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                ...file,
                status: MasterTemplateUploadStatus.UPLOADING
            });
        },

        updatePostData() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4_EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4_FEATURE_CATEGORIES.MASTER_TEMPLATES,
                featureContext: GA4_FEATURE_CONTEXTS.ADD_TEMPLATE
            });
            // Required to manually add in the Key for some reason.
            if (!this.uploadConfig || !this.uploadConfig.postData) {
                return;
            }

            const data = JSON.parse(this.uploadConfig.postData);
            data.key = `${this.uploadConfig.filePrefix}/\${filename}`;
            this.formData = data;
        }
    },

    apollo: {
        masterTemplateUploadConfig: {
            query: clientMasterTemplatesUploadConfigQuery,
            variables() {
                return {
                    clientId: this.clientId
                };
            },
            skip() {
                if (!this.canManageMasterTemplates || !this.clientId) {
                    return true;
                }

                if (this.uploadConfigObject?.clientId === this.clientId) {
                    // Check if the one we have is expired or not
                    if (this.uploadConfig && uploadConfigExpired(this.uploadConfig)) {
                        return false;
                    }

                    this.isLoading = false;

                    return true;
                }

                return false;
            },
            manual: true,
            result({ data }) {
                this.$store.dispatch(MasterTemplateLibraryAction.SetUploadConfig, {
                    clientId: this.clientId,
                    config: data.masterTemplateUploadConfig
                });

                this.isLoading = false;
            },
            error(e: Error) {
                bus.$emit("apolloErrorEvent", e);
            }
        }
    }
});
</script>
